import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Building2, Lock, Mail, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { getErrorMessage } from '@/lib/api-error';
import {
  EMPTY_FORM_ERRORS,
  fieldError,
  formErrorsFromApiError,
  type FormErrors,
} from '@/lib/form-errors';
import { ROUTES } from '@/routes/paths';
import { AuthCard } from './AuthCard';
import { FormErrorAlert } from './FormErrorAlert';
import { resolveAuthRedirect } from './auth-redirect';
import {
  validateEmail,
  validateNewPassword,
  validateNombre,
  validateNombreEmpresa,
} from './auth-validation';
import { useRegister } from './use-register';

/**
 * Paso 2 del plan (`docs/FRONTEND_PLAN.md` §4): creación de cuenta.
 *
 * Endpoint: `POST /api/auth/register` (público) → `{ token, user, tenants }`. El backend crea
 * el usuario Y su primer tenant, donde queda como ADMIN, así que al responder bien ya hay
 * sesión utilizable: no hace falta un login extra.
 *
 * Decisiones:
 * - El campo "Repite la contraseña" es SOLO del cliente (el backend no lo acepta): evita que
 *   una errata deje la cuenta con una contraseña que el usuario no conoce.
 * - `nombre` es opcional en el contrato: si se deja vacío se omite del body.
 * - Igual que en el login, los mensajes de validación son los nuestros (`auth-validation.ts`)
 *   y el mensaje del backend (409 "ya existe una cuenta con este email") sale en el `Alert`.
 */

/** Campos que existen en el backend: sirven para asociar los mensajes de Zod. */
const FIELDS = ['email', 'password', 'nombre', 'nombreEmpresa'] as const;

export function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const registerMutation = useRegister();

  const [nombre, setNombre] = useState('');
  const [nombreEmpresa, setNombreEmpresa] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /** Al escribir se limpia el error de ese campo (y el del espejo de la contraseña). */
  function clearError(...fields: string[]) {
    setSubmitError(null);
    setErrors((current) => {
      const hasErrors = fields.some((field) => current.fieldErrors[field]);
      if (!hasErrors && current.formMessages.length === 0) return current;

      const fieldErrors = { ...current.fieldErrors };
      for (const field of fields) delete fieldErrors[field];
      return { formMessages: [], fieldErrors };
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const confirmError = !confirmPassword
      ? 'Repite la contraseña'
      : password === confirmPassword
        ? undefined
        : 'Las contraseñas no coinciden';

    const clientErrors: Record<string, string | undefined> = {
      nombre: validateNombre(nombre),
      nombreEmpresa: validateNombreEmpresa(nombreEmpresa),
      email: validateEmail(email),
      password: validateNewPassword(password),
      confirmPassword: confirmError,
    };

    const fieldErrors: Record<string, string[]> = {};
    for (const [field, message] of Object.entries(clientErrors)) {
      if (message) fieldErrors[field] = [message];
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrors({ formMessages: [], fieldErrors });
      setSubmitError(null);
      return;
    }

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      await registerMutation.mutateAsync({
        email: email.trim(),
        password,
        nombreEmpresa: nombreEmpresa.trim(),
        ...(nombre.trim() ? { nombre: nombre.trim() } : {}),
      });

      navigate(resolveAuthRedirect(location.state), { replace: true });
    } catch (error) {
      setErrors(formErrorsFromApiError(error, FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }

  const nombreError = fieldError(errors, 'nombre');
  const nombreEmpresaError = fieldError(errors, 'nombreEmpresa');
  const emailError = fieldError(errors, 'email');
  const passwordError = fieldError(errors, 'password');
  const confirmError = fieldError(errors, 'confirmPassword');

  return (
    <AuthCard
      title="Crear cuenta"
      description="Con tu cuenta nace tu negocio: quedas como administrador y ya puedes conectar WhatsApp, cargar el catálogo y dejar que el bot venda."
      footer={
        <>
          ¿Ya tienes cuenta?{' '}
          <Link to={ROUTES.login} className="font-medium text-brand-700 hover:underline">
            Iniciar sesión
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormErrorAlert message={submitError} errors={errors} />

        {/* En pantallas anchas los dos nombres comparten fila; en móvil van uno debajo del otro. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Tu nombre"
            htmlFor="nombre"
            error={nombreError}
            hint="Cómo te llamas (opcional)."
          >
            <Input
              id="nombre"
              name="nombre"
              autoComplete="name"
              placeholder="Ana Pérez"
              invalid={Boolean(nombreError)}
              aria-describedby={nombreError ? 'nombre-error' : undefined}
              value={nombre}
              onChange={(event) => {
                setNombre(event.target.value);
                clearError('nombre');
              }}
            />
          </Field>

          <Field
            label="Nombre del negocio"
            htmlFor="nombreEmpresa"
            required
            error={nombreEmpresaError}
            hint="Donde viven tus productos, conversaciones y pedidos."
          >
            <Input
              id="nombreEmpresa"
              name="nombreEmpresa"
              autoComplete="organization"
              placeholder="Mi tienda"
              leftIcon={<Building2 size={16} />}
              invalid={Boolean(nombreEmpresaError)}
              aria-describedby={nombreEmpresaError ? 'nombreEmpresa-error' : undefined}
              value={nombreEmpresa}
              onChange={(event) => {
                setNombreEmpresa(event.target.value);
                clearError('nombreEmpresa');
              }}
            />
          </Field>
        </div>

        <Field label="Email" htmlFor="email" required error={emailError}>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="tu@negocio.com"
            leftIcon={<Mail size={16} />}
            invalid={Boolean(emailError)}
            aria-describedby={emailError ? 'email-error' : undefined}
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              clearError('email');
            }}
          />
        </Field>

        <Field
          label="Contraseña"
          htmlFor="password"
          required
          error={passwordError}
          hint="Mínimo 8 caracteres."
        >
          <PasswordInput
            id="password"
            name="password"
            autoComplete="new-password"
            placeholder="Tu contraseña"
            leftIcon={<Lock size={16} />}
            invalid={Boolean(passwordError)}
            aria-describedby={passwordError ? 'password-error' : undefined}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              // Al cambiarla, el aviso del campo espejo deja de ser válido.
              clearError('password', 'confirmPassword');
            }}
          />
        </Field>

        <Field
          label="Repite la contraseña"
          htmlFor="confirmPassword"
          required
          error={confirmError}
          hint="Solo para confirmar: no se envía al servidor."
        >
          <PasswordInput
            id="confirmPassword"
            name="confirmPassword"
            autoComplete="new-password"
            placeholder="La misma contraseña"
            leftIcon={<Lock size={16} />}
            invalid={Boolean(confirmError)}
            aria-describedby={confirmError ? 'confirmPassword-error' : undefined}
            value={confirmPassword}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
              clearError('confirmPassword');
            }}
          />
        </Field>

        <Button
          type="submit"
          size="lg"
          className="w-full"
          loading={registerMutation.isPending}
          leftIcon={<UserPlus size={16} />}
        >
          Crear cuenta
        </Button>

      </form>
    </AuthCard>
  );
}
